import type { Permission } from '../types';

/** Fixed matrix columns; any other action is shown as an extra toggle on the row */
export const MATRIX_COLUMNS = [
  { key: 'view', label: 'Ver', actions: ['view'] },
  { key: 'create', label: 'Crear', actions: ['create', 'add'] },
  { key: 'edit', label: 'Editar', actions: ['edit'] },
  { key: 'delete', label: 'Eliminar', actions: ['delete', 'remove'] },
] as const;

export type MatrixColumnKey = (typeof MATRIX_COLUMNS)[number]['key'];

export interface MatrixResource {
  resource: string;
  label: string;
  /** true when the permission follows the district/region of the assignment */
  scoped: boolean;
  columns: Partial<Record<MatrixColumnKey, Permission>>;
  extras: Permission[];
  /** Every code of the resource */
  codes: string[];
  /** The resource's "view" code, implied by any other action */
  viewCode: string | null;
}

export interface MatrixModule {
  module: string;
  resources: MatrixResource[];
  codes: string[];
}

const COLUMN_OF_ACTION = new Map<string, MatrixColumnKey>(
  MATRIX_COLUMNS.flatMap((c) => c.actions.map((a) => [a, c.key] as const)),
);

/** Groups the catalog by module (module_order) and resource (catalog order) */
export function buildMatrix(catalog: readonly Permission[]): MatrixModule[] {
  const modules = new Map<string, { order: number; resources: Map<string, MatrixResource> }>();

  for (const p of catalog) {
    let mod = modules.get(p.module);
    if (!mod) {
      mod = { order: p.module_order, resources: new Map() };
      modules.set(p.module, mod);
    }
    let res = mod.resources.get(p.resource);
    if (!res) {
      res = { resource: p.resource, label: p.resource_label, scoped: false, columns: {}, extras: [], codes: [], viewCode: null };
      mod.resources.set(p.resource, res);
    }
    res.scoped ||= p.is_scoped;
    res.codes.push(p.code);
    if (p.action === 'view') res.viewCode = p.code;

    const column = COLUMN_OF_ACTION.get(p.action);
    if (column && !res.columns[column]) res.columns[column] = p;
    else res.extras.push(p);
  }

  return [...modules.entries()]
    .sort(([a, ma], [b, mb]) => ma.order - mb.order || a.localeCompare(b))
    .map(([module, m]) => {
      const resources = [...m.resources.values()];
      return { module, resources, codes: resources.flatMap((r) => r.codes) };
    });
}

/** Resource of a code: "event_participant.checkin" -> "event_participant" */
const resourceOf = (code: string) => code.slice(0, code.indexOf('.'));

/**
 * Toggles one code keeping the "view" rule: any action needs the resource's view,
 * and removing view removes every action of that resource.
 */
export function toggleCode(
  selected: ReadonlySet<string>,
  code: string,
  on: boolean,
  resources: readonly MatrixResource[],
): Set<string> {
  const next = new Set(selected);
  const res = resources.find((r) => r.resource === resourceOf(code));

  if (on) {
    next.add(code);
    if (res?.viewCode) next.add(res.viewCode);
  } else if (res && code === res.viewCode) {
    res.codes.forEach((c) => next.delete(c));
  } else {
    next.delete(code);
  }
  return next;
}

/** Turns a whole group of codes on or off (row, module, everything) */
export function setCodes(selected: ReadonlySet<string>, codes: readonly string[], on: boolean): Set<string> {
  const next = new Set(selected);
  codes.forEach((c) => (on ? next.add(c) : next.delete(c)));
  return next;
}

export type CheckState = boolean | 'indeterminate';

export function groupState(selected: ReadonlySet<string>, codes: readonly string[]): CheckState {
  const count = codes.filter((c) => selected.has(c)).length;
  if (count === 0) return false;
  return count === codes.length ? true : 'indeterminate';
}

/** Codes added and removed compared with the saved set */
export function diffCodes(saved: ReadonlySet<string>, current: ReadonlySet<string>) {
  return {
    added: [...current].filter((c) => !saved.has(c)),
    removed: [...saved].filter((c) => !current.has(c)),
  };
}

/** Case- and accent-insensitive match on module, resource and action labels */
export function matchesSearch(resource: MatrixResource, module: string, query: string): boolean {
  const q = normalize(query.trim());
  if (!q) return true;
  const haystack = [module, resource.label, ...Object.values(resource.columns).map((p) => p?.action_label ?? ''), ...resource.extras.map((p) => p.action_label)]
    .map(normalize)
    .join(' ');
  return q.split(/\s+/).every((token) => haystack.includes(token));
}

export function normalize(text: string): string {
  return text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Suggested role code from its name: "Tesorero Distrital" -> "TESORERO_DISTRITAL" */
export function roleCodeFromName(name: string): string {
  return normalize(name)
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[0-9_]+/, '')
    .slice(0, 50);
}

export const ROLE_CODE_PATTERN = /^[A-Z][A-Z0-9_]{1,49}$/;
